"use client";

import { useCallback, useEffect, useState } from "react";
import { parseAsString, useQueryState } from "nuqs";
import { useDebounceCallback } from "usehooks-ts";
import { usePipelineTableStore } from "@/features/jobs/store/pipeline/table";
import { usePipelineViewStore } from "@/features/jobs/store/pipeline/view";

const SEARCH_DEBOUNCE_MS = 300;

const qParser = parseAsString.withDefault("").withOptions({
  history: "replace",
  scroll: false,
  shallow: true,
  clearOnDefault: true,
});

/**
 * Pipeline table search backed by `?q=`.
 *
 * - `searchQuery` is the committed value (URL) used for API fetches — available
 *   on the first render when the URL already has `q`, so the table does not
 *   fetch empty then refetch.
 * - `draft` / `setDraft` drive the input UI; commits to the URL are debounced.
 * - Zustand `filterSearch` stays synced for bulk/selection helpers.
 */
export function usePipelineSearchQuery() {
  const [searchQuery, setSearchQuery] = useQueryState("q", qParser);
  const setFilterSearch = usePipelineViewStore((s) => s.setFilterSearch);
  const setPage = usePipelineTableStore((s) => s.setPage);

  const [draft, setDraftState] = useState(searchQuery);

  useEffect(() => {
    setFilterSearch(searchQuery);
  }, [searchQuery, setFilterSearch]);

  useEffect(() => {
    setDraftState(searchQuery);
  }, [searchQuery]);

  const commit = useCallback(
    (value: string) => {
      const next = value.trim();
      setPage(1);
      void setSearchQuery(next);
    },
    [setPage, setSearchQuery],
  );

  const debouncedCommit = useDebounceCallback(commit, SEARCH_DEBOUNCE_MS);

  useEffect(() => debouncedCommit.cancel, [debouncedCommit]);

  const setDraft = useCallback(
    (value: string) => {
      setDraftState(value);
      debouncedCommit(value);
    },
    [debouncedCommit],
  );

  const clearSearch = useCallback(() => {
    debouncedCommit.cancel();
    setDraftState("");
    setPage(1);
    void setSearchQuery("");
  }, [debouncedCommit, setPage, setSearchQuery]);

  return {
    searchQuery,
    draft,
    setDraft,
    clearSearch,
  };
}
